/**
 * `LexiunSpinner` — animated wrapper around `LexiunMark` for the
 * "Lexiun is computing" loading state on the `/home` check-in
 * screen's `LexiunStatusCard`.
 *
 * Four CSS-transform-based animation variants, all defined in
 * `index.css` under `@keyframes lexiun-*`. Each runs continuously
 * at a calm cadence (2.8–3.6s per cycle); `prefers-reduced-motion`
 * disables the animation and renders the static mark at 0.85 opacity.
 *
 *   - `rotate`  — slow continuous rotation, classic spinner cadence.
 *   - `breathe` — scale + opacity pulse, no rotation.
 *   - `orbit`   — rotate + scale + opacity wave, organic combo.
 *   - `swirl`   — ease-in-out rotate with scale pulses at quarter
 *                 marks; more rhythmic feel.
 */

import type { SVGAttributes } from "react";
import { LexiunMark } from "@/components/home/LexiunMark";
import { cn } from "@/lib/utils";

export type LexiunSpinnerVariant = "rotate" | "breathe" | "orbit" | "swirl";

interface LexiunSpinnerProps
  extends Omit<SVGAttributes<SVGSVGElement>, "className"> {
  variant?: LexiunSpinnerVariant;
  className?: string;
  /** When `false`, the spinner renders the static `LexiunMark` with no
   *  animation class — same component, same DOM, just frozen. Parent
   *  controls when to animate (e.g. only while an LLM call is in
   *  flight). Defaults to `true`. */
  playing?: boolean;
}

const VARIANT_CLASS: Record<LexiunSpinnerVariant, string> = {
  rotate: "animate-lexiun-rotate",
  breathe: "animate-lexiun-breathe",
  orbit: "animate-lexiun-orbit",
  swirl: "animate-lexiun-swirl",
};

export function LexiunSpinner({
  variant = "orbit",
  className,
  playing = true,
  ...rest
}: LexiunSpinnerProps) {
  return (
    <LexiunMark
      data-testid="lexiun-spinner"
      data-variant={variant}
      data-playing={playing}
      className={cn(playing && VARIANT_CLASS[variant], className)}
      {...rest}
    />
  );
}
